import { ForwardedRef, forwardRef, useContext, useEffect, useRef } from 'react'
import hash from 'stable-hash'
import { get } from 'lodash-es'

import { MapContext } from '@/feature/map/context/map'

export type MapShapes =
  | typeof google.maps.Circle
  | typeof google.maps.Polygon
  | typeof google.maps.Polyline
  | typeof google.maps.Rectangle

export type MapShapeProps<T extends MapShapes> = {
  shape: T
  parameters: Omit<ConstructorParameters<T>[0], 'map'>
  map?: google.maps.Map | null
}

export const MapShapeRender = <T extends MapShapes>(
  props: MapShapeProps<T>,
  ref: ForwardedRef<InstanceType<T>>,
) => {
  const { shape, parameters } = props

  // Map is required to specified or omitted leaving the hook order intact
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const map = 'map' in props ? props.map : useContext(MapContext)?.map
  const shapeRef = useRef<InstanceType<T>>(undefined)

  const parametersHash = hash(parameters)

  useEffect(() => {
    if (map) {
      const shapeInst = new shape({ map, ...parameters }) as InstanceType<T>
      shapeRef.current = shapeInst

      if (ref) {
        if (typeof ref === 'function') {
          ref(shapeInst)
        } else {
          ref.current = shapeInst
        }
      }

      return () => {
        shapeInst.setMap(null)
      }
    }
  }, [map, shape])

  useEffect(() => {
    const shape = shapeRef.current
    if (shape) {
      shape.setMap(map ?? null)
      shape.setOptions(parameters)
    }
  }, [map, parametersHash])

  useEffect(() => {
    const shape = shapeRef.current
    if (shape instanceof google.maps.Polyline) {
      shape.setPath(get(parameters, 'path', []) as google.maps.LatLng[])
    } else if (shape instanceof google.maps.Polygon) {
      shape.setPaths(get(parameters, 'paths', []) as google.maps.LatLng[][])
    }
  }, [map, get(parameters, 'path'), get(parameters, 'paths')])

  return null
}
export const MapShape = forwardRef(MapShapeRender)
